import { useState, useRef, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bot, Send, X, Sparkles, Loader2, MessageCircle, History, Search, Pin, PinOff,
  Pencil, Trash2, Check, ArrowLeft, MessageSquare, FileDown, FileSpreadsheet,
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useAiChatStore } from '../../stores/aiChatStore'
import { aiService } from '../../services/aiService'
import { toast, confirm } from '../../stores/uiStore'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import MiniMarkdown from '../../utils/miniMarkdown'
import { useAppStore } from '../../stores/appStore'
import { exportAnswerExcel, exportAnswerPdf, answerHasTable } from './chatExport'

// Son açık konuşma tarayıcıda hatırlanır (kullanıcı bazlı) — sayfa yenilense de kaldığı yerden sürer.
const SESSION_KEY = (uid) => `vertex.ai.session.${uid}`
const readStoredSession = (uid) => { try { return localStorage.getItem(SESSION_KEY(uid)) || null } catch { return null } }
const storeSession = (uid, id) => {
  try { id ? localStorage.setItem(SESSION_KEY(uid), id) : localStorage.removeItem(SESSION_KEY(uid)) } catch { /* özel pencere */ }
}

function groupLabel(iso) {
  if (!iso) return 'Daha eski'
  const d = new Date(iso)
  const now = new Date()
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((day(now) - day(d)) / 86400000)
  if (diff <= 0) return 'Bugün'
  if (diff === 1) return 'Dün'
  if (diff < 7) return 'Bu hafta'
  if (diff < 30) return 'Bu ay'
  return 'Daha eski'
}
const GROUP_ORDER = ['Sabitlenenler', 'Bugün', 'Dün', 'Bu hafta', 'Bu ay', 'Daha eski']

function fmtTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  return sameDay
    ? d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })
}

function SessionRow({ s, active, onOpen, onPin, onRename, onDelete }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(s.title || '')
  const commit = () => {
    const v = val.trim()
    setEditing(false)
    if (v && v !== s.title) onRename(v)
    else setVal(s.title || '')
  }
  return (
    <div
      className={`group flex items-start gap-2 px-3 py-2.5 rounded-xl cursor-pointer border transition-colors ${
        active ? 'bg-primary-50 border-primary-200' : 'bg-white border-transparent hover:bg-gray-50 hover:border-gray-200'
      }`}
      onClick={() => !editing && onOpen()}
    >
      <MessageSquare size={15} className={`mt-0.5 shrink-0 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit() }
              if (e.key === 'Escape') { setEditing(false); setVal(s.title || '') }
            }}
            onBlur={commit}
            maxLength={120}
            className="w-full text-sm px-2 py-1 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        ) : (
          <div className="text-sm text-gray-900 truncate font-medium">{s.title || 'Yeni konuşma'}</div>
        )}
        {!editing && s.preview && (
          <div className="text-[11px] text-gray-500 truncate mt-0.5">{s.preview}</div>
        )}
        <div className="text-[10px] text-gray-400 mt-0.5 tabular-nums">
          {fmtTime(s.updated_at)} · {s.message_count} mesaj
        </div>
      </div>
      <div className={`flex items-center gap-0.5 shrink-0 ${s.is_pinned ? '' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
        <button
          title={s.is_pinned ? 'Sabitlemeyi kaldır' : 'Sabitle'}
          onClick={(e) => { e.stopPropagation(); onPin(!s.is_pinned) }}
          className={`p-1 rounded-md hover:bg-gray-200 ${s.is_pinned ? 'text-amber-600' : 'text-gray-400'}`}
        >
          {s.is_pinned ? <Pin size={13} className="fill-current" /> : <Pin size={13} />}
        </button>
        <button
          title="Yeniden adlandır"
          onClick={(e) => { e.stopPropagation(); setVal(s.title || ''); setEditing(true) }}
          className="p-1 rounded-md hover:bg-gray-200 text-gray-400"
        >
          {editing ? <Check size={13} /> : <Pencil size={13} />}
        </button>
        <button
          title="Sil"
          onClick={(e) => { e.stopPropagation(); onDelete() }}
          className="p-1 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-600"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  )
}

function HistoryPanel({ activeId, onOpen, onNew }) {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const dq = useDebouncedValue(q, 300)
  const { data, isLoading } = useQuery({
    queryKey: ['ai-sessions', dq],
    queryFn: () => aiService.listSessions(dq),
    staleTime: 30 * 1000,
  })
  const sessions = data?.data?.sessions || []
  const invalidate = () => qc.invalidateQueries({ queryKey: ['ai-sessions'] })

  const groups = useMemo(() => {
    const g = {}
    for (const s of sessions) {
      const k = s.is_pinned ? 'Sabitlenenler' : groupLabel(s.updated_at)
      ;(g[k] = g[k] || []).push(s)
    }
    return GROUP_ORDER.filter((k) => g[k]?.length).map((k) => [k, g[k]])
  }, [sessions])

  const pin = async (s, v) => {
    try { await aiService.updateSession(s.id, { is_pinned: v }); invalidate() }
    catch (e) { toast.error('Hata', e.response?.data?.detail || e.message) }
  }
  const rename = async (s, title) => {
    try { await aiService.updateSession(s.id, { title }); invalidate() }
    catch (e) { toast.error('Hata', e.response?.data?.detail || e.message) }
  }
  const del = async (s) => {
    const ok = await confirm({
      title: 'Konuşma silinsin mi?',
      message: `"${s.title || 'Yeni konuşma'}" ve içindeki ${s.message_count} mesaj kalıcı olarak silinecek.`,
      confirmLabel: 'Sil',
      variant: 'danger',
    })
    if (!ok) return
    try {
      await aiService.deleteSession(s.id)
      invalidate()
      if (activeId && s.id.toLowerCase() === String(activeId).toLowerCase()) onNew()
      toast.success('Konuşma silindi')
    } catch (e) { toast.error('Hata', e.response?.data?.detail || e.message) }
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white">
      <div className="px-4 pt-3 pb-2 space-y-2 border-b border-gray-100">
        <button onClick={onNew}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white rounded-xl bg-gradient-to-br from-primary-500 to-violet-600 hover:opacity-90">
          <MessageCircle size={15} /> Yeni konuşma
        </button>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Konuşmalarda ara…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3">
        {isLoading ? (
          <div className="space-y-2 p-1">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-12">
            {dq ? 'Eşleşen konuşma yok.' : 'Henüz kayıtlı konuşma yok. İlk sorunla birlikte burada görünür.'}
          </div>
        ) : groups.map(([label, items]) => (
          <div key={label}>
            <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider px-2 mb-1">{label}</div>
            <div className="space-y-0.5">
              {items.map((s) => (
                <SessionRow
                  key={s.id}
                  s={s}
                  active={!!activeId && s.id.toLowerCase() === String(activeId).toLowerCase()}
                  onOpen={() => onOpen(s)}
                  onPin={(v) => pin(s, v)}
                  onRename={(title) => rename(s, title)}
                  onDelete={() => del(s)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const PRESET_QUESTIONS = {
  ADMIN: [
    'Bu ay toplam ciro ne kadar?',
    'Bu yıl teklif kazan/kaybet oranı?',
    'Kaybolma riski yüksek büyük müşterilerim?',
    'Son 12 ayda düşüş gösteren müşteriler?',
    'Bu hafta en çok satan 5 ürün?',
    'Banka nakit pozisyonum?',
    'Bekleyen teklif onayı var mı?',
    'En büyük 10 müşterim kim?',
    'Satışçı bazlı win rate?',
    'Vadesi geçen alacak toplamı?',
  ],
  GM: [
    'Bu ay toplam ciro ne kadar?',
    'YTD ciro ve trend?',
    'Bu yıl teklif kazan/kaybet oranı?',
    'Satışçı bazlı win rate?',
    'Kaybolma riski yüksek büyük müşterilerim?',
    'Bekleyen teklif onayları?',
    'Banka nakit pozisyonu?',
    'En büyük 10 müşteri ciro bazında?',
    'Son 12 ayda düşüş gösteren müşteriler?',
    'En çok satan 10 ürün son 90 gün?',
  ],
  SALES_MGR: [
    'Bu ay takımım ne kadar sattı?',
    'Takımımın win rate ne?',
    'Kaybolma riski yüksek müşterilerim?',
    'En çok satan 10 ürün?',
    'Bekleyen teklif onayları?',
    'Son 7 gün ciro özeti?',
    'Satışçı bazlı performans karşılaştırma?',
    'Son 12 ayda düşüş gösteren müşteriler?',
    'Müşteri X 360 analizi?',
    'Fiyat duyarlı Siemens alıcısı müşteriler?',
  ],
  FIN_MGR: [
    'Banka nakit pozisyonum?',
    'Bu ay en büyük 10 alacak?',
    'Vadesi geçen alacak toplamı?',
    'Son 7 gün tahsilat özeti?',
    'Müşteri X bakiyesi ve risk durumu?',
    'En riskli 10 alacak?',
    'Bu ay toplam ciro?',
    'Bekleyen finans onayları?',
  ],
  WH_MGR: [
    'SIEMENS stoğu ne kadar?',
    '6ES7 ürünleri stokta var mı?',
    'Hareketsiz stok listesi?',
    'En çok satılan ürünler stokta?',
    'Stok değeri toplam?',
    'Düşük stoklu kritik ürünler?',
    'Son 30 gün hareket görmemiş ürünler?',
  ],
  SALES: [
    'Bu ay ne kadar sattım?',
    'Benim win rate ne?',
    'En çok sattığım 5 ürün?',
    'Müşterimin bakiyesi nedir?',
    'Benim bekleyen tekliflerim?',
    'En iyi müşterilerim?',
    'Kaybolma riskli müşterilerim?',
    'Müşteri X 360 analizi?',
    'Son 90 gün performans özeti?',
    'Fiyat duyarlı Siemens müşterilerim?',
  ],
  ACCT: [
    'Banka nakit pozisyonu?',
    'Bu ay tahsilat özeti?',
    'Vadesi geçen alacaklar?',
    'Müşteri X bakiyesi?',
    'En büyük 10 alacak?',
  ],
}
const rolePresets = (role) => PRESET_QUESTIONS[role] || PRESET_QUESTIONS.ADMIN

// Yanıtın altındaki indirme düğmeleri — asistan dosya üretmez, kullanıcı gördüğü yanıtı indirir.
function AnswerActions({ m, question, bodyRef }) {
  const company = useAppStore((s) => s.activeCompany)
  const [busy, setBusy] = useState('')
  const hasTable = useMemo(() => answerHasTable(m.content), [m.content])
  const run = async (kind) => {
    setBusy(kind)
    try {
      if (kind === 'xlsx') {
        await exportAnswerExcel(m.content, { title: 'Asistan yanıtı' })
      } else {
        const how = await exportAnswerPdf(bodyRef.current?.innerHTML || '', {
          title: 'Asistan yanıtı', question, company: company && company !== '*' ? company : undefined,
        })
        if (how === 'print') toast.info('PDF yazdır penceresinde', 'Hedef olarak "PDF olarak kaydet"i seçin.')
      }
    } catch (e) {
      toast.error('İndirilemedi', e?.response?.data?.detail || e.message)
    } finally {
      setBusy('')
    }
  }
  const btn = 'inline-flex items-center gap-1 px-2 py-1 rounded-md border border-gray-200 text-[11px] text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50'
  return (
    <div className="mt-2 flex items-center gap-1.5">
      <button type="button" className={btn} disabled={!!busy} onClick={() => run('pdf')} title="Yanıtı PDF olarak indir">
        {busy === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} PDF
      </button>
      <button type="button" className={btn} disabled={!!busy} onClick={() => run('xlsx')}
              title={hasTable ? 'Yanıttaki tablo(lar)ı Excel olarak indir' : 'Yanıtı Excel olarak indir'}>
        {busy === 'xlsx' ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Excel
      </button>
    </div>
  )
}

function MessageRow({ m, user, question }) {
  const bodyRef = useRef(null)
  const displayName = user.display_name || user.username || 'Kullanıcı'
  const initials = displayName.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()
  if (m.role === 'user') {
    return (
      <div className="flex gap-3 px-5 py-4 bg-gray-50/30">
        <div className="w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-semibold shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0 pt-1 text-sm text-gray-900 whitespace-pre-wrap break-words">
          {m.content}
        </div>
      </div>
    )
  }
  return (
    <div className="flex gap-3 px-5 py-4 bg-white">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-violet-600 text-white flex items-center justify-center shrink-0">
        <Bot size={16} />
      </div>
      <div className={`flex-1 min-w-0 text-sm leading-relaxed ${m.error ? 'text-red-800' : 'text-gray-800'}`}>
        {m.error ? <p className="whitespace-pre-wrap">{m.content}</p> : (
          <>
            <div ref={bodyRef}><MiniMarkdown text={m.content} /></div>
            {String(m.content || '').trim() && <AnswerActions m={m} question={question} bodyRef={bodyRef} />}
          </>
        )}
      </div>
    </div>
  )
}

export default function ChatWidget() {
  const user = useAuthStore((s) => s.user)
  const open = useAiChatStore((s) => s.open)
  const closeChat = useAiChatStore((s) => s.closeChat)
  const pendingQuestion = useAiChatStore((s) => s.pendingQuestion)
  const consumePending = useAiChatStore((s) => s.consumePending)

  const qc = useQueryClient()
  const uid = user?.id
  const [mounted, setMounted] = useState(false)
  const [sessionId, setSessionId] = useState(() => (uid ? readStoredSession(uid) : null))
  const [sessionTitle, setSessionTitle] = useState('')
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [view, setView] = useState('chat') // 'chat' | 'history'
  const endRef = useRef(null)
  const restoredRef = useRef(false)

  useEffect(() => {
    if (open) {
      const t = requestAnimationFrame(() => setMounted(true))
      const onKey = (e) => e.key === 'Escape' && closeChat()
      window.addEventListener('keydown', onKey)
      return () => {
        cancelAnimationFrame(t)
        window.removeEventListener('keydown', onKey)
        setMounted(false)
      }
    }
  }, [open, closeChat])

  useEffect(() => {
    if (open && view === 'chat') endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, open, view])

  // Kaydedilmiş konuşmayı aç (geçmişten ya da açılışta hatırlanan son konuşma)
  const openSession = async (s) => {
    setLoadingHistory(true)
    setView('chat')
    try {
      const res = await aiService.sessionMessages(s.id)
      const msgs = (res?.data?.messages || []).map((m) => ({ role: m.role, content: m.content || '' }))
      setSessionId(s.id)
      setSessionTitle(s.title || '')
      setMessages(msgs)
      storeSession(uid, s.id)
      if (msgs.length === 0) {
        // Silinmiş / başkasının oturumu: sunucu boş döner → hatırlanan kimlik bırakılır
        setSessionId(null); setSessionTitle(''); storeSession(uid, null)
      }
    } catch (e) {
      toast.error('Konuşma açılamadı', e.response?.data?.detail || e.message)
    } finally {
      setLoadingHistory(false)
    }
  }

  // İlk açılışta tarayıcıda hatırlanan son konuşmayı geri getir (bir kez)
  useEffect(() => {
    if (!open || restoredRef.current || !uid) return
    restoredRef.current = true
    const stored = readStoredSession(uid)
    if (stored && messages.length === 0) openSession({ id: stored, title: '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, uid])

  // Geri getirilen konuşmanın başlığı listeden okunur (mesaj ucu başlık taşımaz)
  const { data: sessData } = useQuery({
    queryKey: ['ai-sessions', ''],
    queryFn: () => aiService.listSessions(''),
    staleTime: 30 * 1000,
    enabled: !!user && open && !!sessionId && !sessionTitle,
  })
  useEffect(() => {
    if (sessionId && !sessionTitle) {
      const hit = (sessData?.data?.sessions || []).find((s) => s.id.toLowerCase() === String(sessionId).toLowerCase())
      if (hit?.title) setSessionTitle(hit.title)
    }
  }, [sessData, sessionId, sessionTitle])

  // Pending question auto-send (preset tıklamadan gelir)
  useEffect(() => {
    if (open && pendingQuestion && !sending) {
      const q = pendingQuestion
      consumePending()
      send(q)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingQuestion])

  // Preset sorular — backend'den (her zaman çağrılmalı, hook order)
  const { data: presetData } = useQuery({
    queryKey: ['ai-preset-questions', user?.role],
    queryFn: () => aiService.presetQuestions(),
    staleTime: 5 * 60 * 1000,
    enabled: !!user,
  })

  if (!user || !open) return null

  const send = async (text) => {
    const q = (text || input).trim()
    if (!q || sending) return
    setMessages((m) => [...m, { role: 'user', content: q }])
    setInput('')
    setSending(true)
    if (view !== 'chat') setView('chat')
    try {
      const res = await aiService.chat({ session_id: sessionId, message: q })
      const d = res.data || {}
      if (d.session_id) {
        setSessionId(d.session_id)
        storeSession(uid, d.session_id)
        if (!sessionTitle) setSessionTitle(q.length > 120 ? q.slice(0, 119) + '…' : q)
      }
      setMessages((m) => [...m, { role: 'assistant', content: d.content || '' }])
      qc.invalidateQueries({ queryKey: ['ai-sessions'] })
    } catch (e) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: `❌ Hata: ${e.response?.data?.detail || e.message}`,
        error: true,
      }])
    } finally {
      setSending(false)
    }
  }

  const newChat = () => {
    setSessionId(null)
    setSessionTitle('')
    setMessages([])
    setInput('')
    setView('chat')
    storeSession(uid, null)
  }

  const backendPresets = presetData?.data?.items || []
  const presets = backendPresets.length > 0
    ? backendPresets.slice(0, 12).map(q => q.text)
    : rolePresets(user.role)

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[90] bg-black/30 backdrop-blur-sm transition-opacity ${mounted ? 'opacity-100' : 'opacity-0'}`}
        onClick={closeChat}
      />
      {/* Right-side panel */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-[91] w-[480px] max-w-full bg-white shadow-2xl border-l border-gray-100 flex flex-col transition-transform duration-200 ${mounted ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 bg-white">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-violet-600 text-white flex items-center justify-center shadow-sm">
            <Sparkles size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900">
              {view === 'history' ? 'Konuşma geçmişi' : 'VERTEX AI'}
            </div>
            <div className="text-[11px] text-gray-500 truncate" title={sessionTitle || undefined}>
              {view === 'history'
                ? 'Kaydedilen konuşmalar — aç, sabitle, yeniden adlandır'
                : sessionTitle || `${user.role}${user.saldept ? ` · ${user.saldept}` : ''}`}
            </div>
          </div>
          {view === 'history' ? (
            <button onClick={() => setView('chat')} title="Konuşmaya dön"
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
              <ArrowLeft size={16} />
            </button>
          ) : (
            <>
              <button onClick={() => setView('history')} title="Konuşma geçmişi"
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                <History size={16} />
              </button>
              <button onClick={newChat} title="Yeni konuşma"
                      className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
                <MessageCircle size={16} />
              </button>
            </>
          )}
          <button onClick={closeChat} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500">
            <X size={18} />
          </button>
        </div>

        {/* History */}
        {view === 'history' && (
          <HistoryPanel activeId={sessionId} onOpen={openSession} onNew={newChat} />
        )}

        {/* Messages */}
        <div className={`flex-1 overflow-y-auto bg-white ${view === 'history' ? 'hidden' : ''}`}>
          {loadingHistory ? (
            <div className="p-5 space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-gray-200 shrink-0" />
                  <div className="flex-1 space-y-2 pt-1">
                    <div className="h-3 w-3/4 bg-gray-200 rounded" />
                    <div className="h-3 w-1/2 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="p-6 space-y-5">
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-100 to-violet-100 flex items-center justify-center">
                  <Sparkles size={32} className="text-primary-600" />
                </div>
                <p className="text-lg font-semibold text-gray-900">Merhaba {(user.display_name || user.username || '').split(' ')[0]}! 👋</p>
                <p className="text-sm text-gray-500 mt-1">Satış, stok, finans verileri hakkında sor</p>
              </div>
              <div className="space-y-2">
                <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wider px-1">
                  Hazır sorular
                </div>
                {presets.map((q, i) => (
                  <button key={i} onClick={() => send(q)}
                          className="block w-full text-left px-4 py-3 text-sm bg-white hover:bg-gray-50 text-gray-700 rounded-xl transition-colors border border-gray-200 hover:border-primary-300 hover:shadow-sm">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {messages.map((m, i) => (
                <MessageRow key={i} m={m} user={user}
                            question={m.role === 'assistant' && messages[i - 1]?.role === 'user' ? messages[i - 1].content : undefined} />
              ))}
              {sending && (
                <div className="flex gap-3 px-5 py-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-violet-600 text-white flex items-center justify-center shrink-0">
                    <Bot size={16} />
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 text-sm pt-1">
                    <Loader2 size={14} className="animate-spin" /> Düşünüyor...
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className={`p-4 border-t border-gray-100 bg-white ${view === 'history' ? 'hidden' : ''}`}>
          <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100 rounded-2xl p-2 transition-all">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              rows={1}
              placeholder="VERTEX AI'ya sor..."
              className="flex-1 px-2 py-1.5 text-sm bg-transparent resize-none focus:outline-none max-h-32 placeholder:text-gray-400"
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || sending}
              className="p-2 bg-gradient-to-br from-primary-500 to-violet-600 hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-xl transition-all shrink-0"
            >
              <Send size={15} />
            </button>
          </div>
          <p className="text-[10px] text-gray-400 mt-2 text-center">
            Enter gönder · Shift+Enter yeni satır · AI cevapları doğrulanmalı
          </p>
        </div>
      </div>
    </>,
    document.body,
  )
}
